import { useId, useState } from 'react';

import { cx } from './cx';

interface MultiSelectChipsProps {
  id?: string;
  value: string[];
  onChange: (next: string[]) => void;
  options: string[];
  placeholder?: string;
}

/** Chip multi-select: pick from known options or type a new one — search-or-add-new. */
export function MultiSelectChips({
  id,
  value,
  onChange,
  options,
  placeholder = 'Search or add new…',
}: MultiSelectChipsProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [query, setQuery] = useState('');

  const suggestions = options.filter(
    (option) =>
      !value.includes(option) && option.toLowerCase().includes(query.trim().toLowerCase()),
  );

  function addValue(next: string) {
    const trimmed = next.trim();
    if (trimmed === '' || value.includes(trimmed)) {
      return;
    }
    onChange([...value, trimmed]);
    setQuery('');
  }

  function removeValue(target: string) {
    onChange(value.filter((entry) => entry !== target));
  }

  return (
    <div className="rfp-chip-select">
      <div className="rfp-chip-select__chips">
        {value.map((entry) => (
          <span className="rfp-chip" key={entry}>
            {entry}
            <button
              aria-label={`Remove ${entry}`}
              className="rfp-chip__remove"
              onClick={() => removeValue(entry)}
              type="button"
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="rfp-chip-select__input"
          id={inputId}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addValue(query);
            }
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          type="text"
          value={query}
        />
      </div>
      {query.trim() !== '' && suggestions.length > 0 && (
        <ul className="rfp-chip-select__suggestions">
          {suggestions.map((option) => (
            <li key={option}>
              <button onClick={() => addValue(option)} type="button">
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
      {query.trim() !== '' && !options.includes(query.trim()) && (
        <p className={cx('rfp-chip-select__hint')}>Press Enter to add "{query.trim()}"</p>
      )}
    </div>
  );
}
